import { Navigate, Outlet } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { useAuth } from '@/features/auth/session';
import { isSuperAdmin } from '@/features/auth/roles';
import { AppLoader } from '@/shared/components/feedback/AppLoader';

/**
 * The operator's own screens, behind the one role that is not a tenant role.
 *
 * <h2>It waits, and that is the whole difficulty</h2>
 *
 * {@link ProtectedRoute} can decide immediately: a token either exists or it does not,
 * and reading it is synchronous. This cannot. The role comes from `/api/v1/account/me`,
 * so on a cold start — somebody opening the catalogue URL directly, or refreshing on it —
 * there is a moment where the session is real and `user` is still `null`.
 *
 * <p>Deciding during that moment sends every super admin to the dashboard on every
 * refresh. So it renders the application's one loader instead and decides when the answer
 * arrives.
 *
 * <h2>Where it sends somebody who may not be here</h2>
 *
 * The dashboard, not sign-in. They are signed in; they are simply not the operator, and
 * bouncing them to a login form invites them to try other credentials for a screen no
 * other credentials would open. `replace`, so Back does not return to a route that will
 * only bounce them again.
 *
 * <h2>This is not the security boundary</h2>
 *
 * Every endpoint behind these screens carries `@SuperAdminOnly` and refuses on its own.
 * This decides what is *shown* — see {@link isSuperAdmin}. A guard that is wrong shows or
 * hides a screen; it cannot give anything.
 */
export function SuperAdminRoute() {
  const { user, status } = useAuth();

  if (status === 'signed-out') {
    // ProtectedRoute above already handles this for nested routes; the check is here too so
    // this component is safe to mount anywhere. One definition of signed-out, shared.
    return <Navigate to={ROUTES.signIn} replace />;
  }

  if (status === 'unknown' || !user) {
    return <AppLoader />;
  }

  return isSuperAdmin(user) ? <Outlet /> : <Navigate to={ROUTES.dashboard} replace />;
}
